import { Component, OnDestroy, inject } from '@angular/core';
import {
  ColDef,
} from 'ag-grid-community';
import { CurrencyPipe } from '@angular/common';
import { FormControl, FormGroup } from '@angular/forms';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatDatepicker } from '@angular/material/datepicker';
import { MomentDateAdapter, MAT_MOMENT_DATE_ADAPTER_OPTIONS } from '@angular/material-moment-adapter';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { SnackbarConstants } from '@lmc-app/shared/constants/snackbar.constant';
import { LoanAccountService } from '@lmc-app/core/services/loan-account.service';
import { Store } from '@ngrx/store';
import { selectUser, State } from '@lmc-app/store';
import { Subject, takeUntil } from 'rxjs';
import { User } from '@lmc-app/shared/interface/user.interface';
import { Loading } from '../../../../applications/loading';

// Depending on whether rollup is used, moment needs to be imported differently.
// Since Moment.js doesn't have a default export, we normally need to import using the `* as`
// syntax. However, rollup creates a synthetic default module and we thus need to import it using
// the `default as` syntax.
import * as _moment from 'moment';
// tslint:disable-next-line:no-duplicate-imports
import { default as _rollupMoment, Moment } from 'moment';

export const MY_FORMATS = {
  parse: {
    dateInput: 'MM/YYYY',
  },
  display: {
    dateInput: 'MM/YYYY',
    monthYearLabel: 'MMM YYYY',
    dateA11yLabel: 'LL',
    monthYearA11yLabel: 'MMMM YYYY',
  },
};

@Component({
  selector: 'app-funding-exception',
  templateUrl: './funding-exception.component.html',
  styleUrls: ['./funding-exception.component.scss'],
  providers: [
    // `MomentDateAdapter` can be automatically provided by importing `MomentDateModule` in your
    // application's root module. We provide it at the component level here, due to limitations of
    // our example generation script.
    {
      provide: DateAdapter,
      useClass: MomentDateAdapter,
      deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS],
    },

    { provide: MAT_DATE_FORMATS, useValue: MY_FORMATS },
  ],
})
export class FundingExceptionComponent implements OnDestroy {
  private readonly _onDestroy = new Subject<void>();
  private userData: User | null = null;
  private readonly _bottomSheet = inject(MatBottomSheet);
  private readonly _snackBar = inject(MatSnackBar);
  protected fundingExceptions: any;
  protected moment = _rollupMoment || _moment;

  inputDateForm = new FormGroup({
    input_date: new FormControl(null),
    target: new FormControl(null),
    num_business_day: new FormControl(null),
  });
  protected columnDefs: ColDef[] = [
    { headerName: 'Funded Date', field: 'funded_date' },
    { headerName: 'Name', field: 'name', filter: "agTextColumnFilter" },
    { headerName: 'Account Number', field: 'account_number' },
    {
      headerName: 'Funded Amount',
      field: 'funded_amount',
      valueGetter: (params) => {
        return this.currencyFormatter.transform(
          params.data.funded_amount,
          'CAD',
          'symbol-narrow'
        );
      },
    },
    { headerName: 'Exception', field: 'exception', filter: "agTextColumnFilter" },
    {
      headerName: 'Status',
      field: 'exception_status',
      filter: "agTextColumnFilter",
      editable: true,
      cellEditor: 'agSelectCellEditor',
      cellEditorParams: {
        values: ['Pending', 'Completed'],
      },
    },
  ];

  constructor(
    private readonly currencyFormatter: CurrencyPipe,
    private loanAccountService: LoanAccountService,
    private store: Store<State>
  ) {
    this.store.select(selectUser)
      .pipe(takeUntil(this._onDestroy))
      .subscribe((user: User | null) => {
        this.userData = user;
      });
    this.inputDateForm.controls.input_date.setValue(new Date());
    const inputDateValue = this.inputDateForm.controls.input_date.value;
    this.getFundingExceptions(inputDateValue.getFullYear(), inputDateValue.getMonth() + 1)
  }

  getFundingExceptions(year, month) {
    this.loanAccountService.getExceptions(year, month).subscribe((res: any) => {
      this.fundingExceptions = res;
    })
  }

  setMonthAndYear(normalizedMonthAndYear: Moment, datepicker: MatDatepicker<Moment>) {
    const year = normalizedMonthAndYear.year();
    const month = normalizedMonthAndYear.month() + 1;
    const inputDate = `${year}-${month.toString().padStart(2, "0")}-02`;
    this.inputDateForm.controls.input_date.setValue(new Date(inputDate));
    this.getFundingExceptions(year, month);
    datepicker.close();
  }

  onCellValueChanged(e) {
    this._bottomSheet.open(Loading, {
      disableClose: true,
      data: { message: 'Saving' },
    });
    const inputDateValue = this.inputDateForm.controls.input_date.value;
    this.loanAccountService.updateLoanAccountException(e.data).subscribe({
      next: () => {
        this.getFundingExceptions(inputDateValue.getFullYear(), inputDateValue.getMonth() + 1);
        this._snackBar.open(
          `Loan account for ${e.data.name} is updated.`,
          SnackbarConstants.ACTION_DISMISS,
          SnackbarConstants.DURATION_DISMISS_SHORT
        );
        this._bottomSheet.dismiss();
      },
      error: (error) => {
        this.getFundingExceptions(inputDateValue.getFullYear(), inputDateValue.getMonth() + 1);
        this._bottomSheet.dismiss();
        console.error(error);
        this._snackBar.open(
          (error.error && (error.error.message && (error.error.message.name || error.error.message))) || 'Error updating loan account.',
          SnackbarConstants.ACTION_DISMISS,
          SnackbarConstants.DURATION_DISMISS_SHORT
        );
      },
    });
  }

  ngOnDestroy(): void {
    this._onDestroy.next();
    this._onDestroy.complete();
  }
}
